import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IGetSubscriptionDetailsResponse } from '../../../../api/subscription';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';

import { formatNumber } from '../../../../utils/numbersFormatting';
import BillingOptions from './BillingOptions';
import MondiaBillingInfo from './MondiaBillingInfo';

interface IBillingTabProps {
  subscriptionDetails?: IGetSubscriptionDetailsResponse;
  isFetching: boolean;
  refetch: () => void;
}

export default function BillingTab({
  subscriptionDetails,
  isFetching,
  refetch,
}: IBillingTabProps) {
  const { t } = useTranslation();
  const { credit_usage } = subscriptionDetails || {};

  useEffect(() => {
    void refetch();
  }, []);

  const creditBalance = credit_usage
    ? credit_usage.subscription_details.creditBalance +
      credit_usage.pack_details.creditBalance
    : 0;

  const billingProvider = subscriptionDetails?.billingDetails?.provider;

  const renderBillingInfo = () => {
    switch (billingProvider) {
      case 'apple':
        return (
          <div className="text-sm text-white">
            {t('account.appleBillingInfo')}{' '}
            <a
              href="https://apps.apple.com/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="underline">
              {t('account.goHere')}
            </a>
            .
          </div>
        );
      case 'google':
        return (
          <div className="text-sm text-white">
            {t('account.googleBillingInfo')}{' '}
            <a
              href="https://play.google.com/store/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="underline">
              {t('account.goHere')}
            </a>
            .
          </div>
        );
      case 'mondia':
        return (
          <MondiaBillingInfo
            expiresAt={subscriptionDetails?.billingDetails?.expiresAt}
            operatorName={subscriptionDetails?.billingDetails?.operatorName}
          />
        );
      case 'recurly':
      case 'none':
      case 'stripe': {
        return (
          <>
            <h5>{t('account.billingInformation')}</h5>
            <hr className=" border-[#32323B] my-4" />
            <BillingOptions billingProvider={billingProvider} />
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex gap-4 md:flex-row flex-col text-left">
        <div className="rounded-regular p-6 tc-mv-blur text-white w-full relative overflow-hidden">
          <div className="flex">
            <div>
              <div className="text-[12px] text-[#C1C1CA]">
                {t('account.yourCurrentPlan')}
              </div>
              <div className="text-2xl font-medium">
                {isFetching ? (
                  <AnimatedSpinner />
                ) : (
                  subscriptionDetails?.billingDetails?.planName
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-regular p-6 tc-mv-blur text-white w-full">
          <div className="text-[12px] text-[#C1C1CA]">
            {t('account.availableCredits')}
          </div>
          <div className=" self-center ml-auto text-2xl font-medium">
            {isFetching ? (
              <AnimatedSpinner />
            ) : (
              creditBalance && formatNumber(creditBalance)
            )}
          </div>
        </div>
      </div>
      {!isFetching && (
        <div className="rounded-regular p-6 tc-mv-blur text-white mt-4 text-left">
          {renderBillingInfo()}
        </div>
      )}
    </>
  );
}
